import {
  CreateCaseDto,
  DataBlockVariant,
  GDTMessageDirection,
  GDTMessageGroup,
  HL7MessageDirection,
  HL7MessageGroup,
  NumberSource,
  QueryFactory,
  SortDirection,
  useEnabledDataBlockTypes,
  VisitListDto,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useTabId } from '@lmt/unicorn-shared/app/components/keep-alive/TabContext';
import {
  caseInformationDataBlockTypeName,
  visitInformationDataBlockTypeName,
} from '@lmt/unicorn-shared/app/components/measurements/common';
import { getLatestCase } from '@lmt/unicorn-shared/app/components/patients/common';

import tableStyles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import {
  getCurrentUrl,
  getTabId,
  useTabs,
} from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { localizeCaseTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';

import { ReactComponent as AddCaseIcon } from '@lmt/unicorn-shared/app/icons/new-case.svg';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { startOfToday } from 'date-fns';
import Logger from 'js-logger';
import queryString from 'query-string';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Freeze } from 'react-freeze';
import { useQueryClient } from '@tanstack/react-query';
import { QueryMetaProvider } from 'react-query-swagger';
import { useLocation, useNavigate } from 'react-router-dom';
import { CaseBlocks } from './components/blocks/CaseBlocks';
import { CaseContents } from './components/case-tree/CaseContents';
import {
  getItemPrefix,
  getTransportStatus,
} from './components/case-tree/common';
import { CaseBar } from './components/navigation/CaseBar';
import styles from './PatientCases.module.scss';
import {
  CaseItem,
  CaseItemType,
  SubCaseItemType,
  TransportInfo,
} from './types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { copyPreviousValues } from '../../../measurements/copy-datablock-field/copy-previous-values-async-helper';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';

interface PatientOverviewProps {
  patientId: number;
  caseId?: number | null;
  currentItemId?: string;
}

export const PatientCases: React.FC<PatientOverviewProps> = React.memo(
  function PatientCases(props) {
    const { patientId, caseId, currentItemId } = props;
    const tabs = useTabs();
    const tabId = useTabId();
    const i18next = useScopedTranslation('patients.cases');
    const dispatch = useUniDispatch();
    const navigate = useNavigate();
    const forms = useGlobalForms();
    const location = useLocation();
    const claims = useClaims();
    const [isCaseBeingDeleted, setCaseBeingDeleted] = useState(false);
    const [isCaseBeingAdded, setCaseBeingAdded] = useState(false);
    const dataBlockTypes = useEnabledDataBlockTypes();
    const { data: gdtSettings } = QueryFactory.GDTSettingsQuery.useGetQuery({
      enabled: claims.permissions.gdt,
    });
    const { data: hl7OutgoingSettings } =
      QueryFactory.HL7OutgoingSettingsQuery.useGetQuery({
        enabled: claims.permissions.hl7,
      });

    const queryClient = useQueryClient();
    const [searchedItem, setSearchedItem] = useState<string | null>(null);

    useEffect(() => {
      const parsedString = queryString.parse(location.search);
      // trying to find element of case (visit, measurement, etc.) in URL that should be focused
      // (i.e. '?item=visit_1_measurement_2')
      const selectedItem = parsedString.item?.toString();
      if (selectedItem) {
        setSearchedItem(selectedItem);
      }
    }, [location.search]);

    const copyPreviousValuesForNewDefaultDataBlocks = useCallback(
      async (caseId: number) => {
        const dataBlocks = await ClientFactory.DataBlockClient.get(
          props.patientId,
          caseId,
        );
        for (const dataBlock of dataBlocks.data) {
          await copyPreviousValues(
            dataBlock.id,
            forms.openCopyDataBlockValue,
            modals,
            queryClient,
          );
        }
      },
      [forms, dispatch, queryClient],
    );

    const addCase = useCallback(async () => {
      try {
        setCaseBeingAdded(true);
        const startDate = startOfToday();
        const newCase: CreateCaseDto = {
          patientId: patientId,
          startDate: startDate,
          endDate: null,
        };
        const createdCase = await ClientFactory.CaseClient.createCase(newCase);
        await copyPreviousValuesForNewDefaultDataBlocks(createdCase.id);

        await casesQuery.refetch().catch((e) => Logger.error(e));
        openCaseTabOrEmpty(createdCase.id);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        setCaseBeingAdded(false);
      }
    }, [copyPreviousValuesForNewDefaultDataBlocks]);

    const modals = useModal();
    const removeCase = useCallback(
      async (caseId: number) => {
        const caseToRemove = casesQuery.data?.data?.find(
          (item) => item.id === caseId,
        );
        const caseTitle = !!caseToRemove
          ? localizeCaseTitle(caseToRemove)
          : localizeCaseTitle({
              id: caseId,
              number: caseId.toString(),
              numberSource: NumberSource.Autogenerated,
              startDate: new Date(),
            });
        const isConfirmed = await modals.showConfirm({
          title: `${i18next.t('confirmation_delete_title')}  ${caseTitle}`,
          text: i18next.t('confirmation_delete_text'),
          okButtonText: i18next.t('confirmation_delete_button'),
          okButtonColor: ButtonColor.SuperDanger,
        });
        if (isConfirmed) {
          try {
            setCaseBeingDeleted(true);
            await ClientFactory.CaseClient.removeCase(caseId);
            await casesQuery.refetch();
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.searchFilesQueryKey(patientId),
            });
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.FileQuery.searchFilesQueryKey(),
            });
            openCaseTabOrEmpty();
          } catch (e) {
            await modals.showError({ text: convertToErrorString(e) });
          } finally {
            setCaseBeingDeleted(false);
          }
        }
      },
      [caseId],
    );

    const openCaseTabOrEmpty = useCallback(
      (caseId?: number, replace = false) => {
        tabs.changeTabPath(
          caseId
            ? pages.patientCases.link({
                patientId: props.patientId,
                caseId: caseId ?? ('' as any),
              })
            : pages.patientCaseLatest.link({ patientId: props.patientId }),
          replace,
        );
      },
      [],
    );
    const { data: patient } = QueryFactory.PatientQuery.useGetPatientQuery(
      props.patientId,
    );
    const casesQuery = QueryFactory.CaseQuery.useGetCasesQuery({
      patientId: props.patientId,
    });
    const isCaseQueriesEnabled =
      !!props.caseId &&
      !casesQuery.isFetching &&
      casesQuery.data &&
      casesQuery.data.data &&
      casesQuery.data.data.findIndex((x) => x.id === props.caseId) > -1;
    const caseQuery = QueryFactory.CaseQuery.useGetCaseQuery(
      { id: props.caseId! },
      {
        enabled: !!props.caseId,
      },
    );
    const visitsQuery =
      QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery(
        {
          patientId: patientId,
          caseId: props.caseId!,
        },
        {
          enabled: isCaseQueriesEnabled,
        },
      );

    const dataBlocksQuery = QueryFactory.DataBlockQuery.useGetQuery(
      {
        caseId: props.caseId,
      },
      {
        enabled: isCaseQueriesEnabled,
      },
    );

    const reopenCase = useCallback(
      async (caseId: number) => {
        try {
          await ClientFactory.CaseClient.reopenCase(caseId);
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockQuery.getQueryKey(),
          });
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      },
      [caseId],
    );

    const { data: hl7TemplateRules } =
      QueryFactory.HL7TemplateRulesQuery.useGetQuery(
        {
          subtenantId: patient?.subtenantId,
          isActive: true,
          messageGroup: HL7MessageGroup.DataBlock,
        },
        {
          enabled: claims.permissions.hl7 && !!patient?.subtenantId,
        },
      );
    const { data: gdtTemplateRules } =
      QueryFactory.GDTTemplateRulesQuery.useGetQuery(
        {
          subtenantId: patient?.subtenantId,
          isActive: true,
          messageGroup: GDTMessageGroup.DataBlock,
        },
        {
          enabled: claims.permissions.gdt && !!patient?.subtenantId,
        },
      );
    const visitInformationDataBlockType = useMemo(() => {
      return dataBlockTypes.find(
        (dataBlockType) =>
          dataBlockType.variant === DataBlockVariant.Visit &&
          dataBlockType.value === visitInformationDataBlockTypeName,
      );
    }, [dataBlockTypes]);

    const hl7MessageQuery = QueryFactory.HL7MessageQuery.useGetMessagesQuery(
      {
        direction: HL7MessageDirection.Outgoing,
        patientId: patientId,
        pagination_Offset: 0,
        pagination_Limit: 200,
        sorting_SortField: 'dateTime',
        sorting_SortDirection: SortDirection.Desc,
      },
      {
        enabled: claims.permissions.hl7,
      },
    );

    const gdtMessageQuery = QueryFactory.GDTMessageQuery.useGetMessagesQuery(
      {
        direction: GDTMessageDirection.Outgoing,
        patientId: patientId,
        pagination_Offset: 0,
        pagination_Limit: 200,
        sorting_SortField: 'dateTime',
        sorting_SortDirection: SortDirection.Desc,
      },
      {
        enabled: claims.permissions.gdt,
      },
    );

    const lastSelectedCaseIdRef = useRef(props.caseId);
    useEffect(
      function updateLastSelectedCaseIdWhenCaseChanges() {
        if (caseId) {
          lastSelectedCaseIdRef.current = caseId;
        }
      },
      [caseId],
    );
    useEffect(
      function openLastSelectedCaseIfNothingIsOpened() {
        if (!casesQuery.data?.data) return;

        const isSelectedCaseRemoved =
          props.caseId &&
          !casesQuery.data.data.find((x) => x.id === props.caseId);

        if (isSelectedCaseRemoved || !props.caseId) {
          if (casesQuery.data.data.length > 0) {
            // if lastSelectedCase was deleted, we need to set it to null
            if (
              lastSelectedCaseIdRef.current &&
              !casesQuery.data.data.find(
                (x) => x.id === lastSelectedCaseIdRef.current,
              )
            ) {
              lastSelectedCaseIdRef.current = undefined;
            }

            const caseIdToSelect =
              lastSelectedCaseIdRef.current ??
              getLatestCase(casesQuery.data.data)?.id;

            openCaseTabOrEmpty(caseIdToSelect, true);
          } else {
            openCaseTabOrEmpty(undefined, true);
          }
        }
      },
      [props.caseId, casesQuery.data?.data?.length],
    );

    function getVariant(typeId: number) {
      return dataBlockTypes.find((type) => type.id === typeId)?.variant;
    }

    function getIsGdtEnabled(typeId: number): boolean {
      if (!gdtSettings?.enabled) {
        return false;
      }
      const templateRule = (gdtTemplateRules ?? []).some(
        (x) => x.dataBlockTypeId === typeId,
      );
      return !!templateRule;
    }

    function getIsHL7Enabled(typeId: number): boolean {
      if (
        !claims.permissions.hl7 ||
        (!hl7OutgoingSettings?.enabled &&
          !hl7OutgoingSettings?.outgoingTransporterEnabled)
      ) {
        return false;
      }
      const templateRule = (hl7TemplateRules ?? []).some(
        (x) => x.dataBlockTypeId === typeId,
      );
      return !!templateRule;
    }

    function getTransportInfo(dataBlockId: number): TransportInfo | undefined {
      let transportInfo: TransportInfo = {
        isGDT: false,
        isHL7: false,
      };
      if (
        claims.permissions.hl7 &&
        (hl7OutgoingSettings?.enabled ||
          hl7OutgoingSettings?.outgoingTransporterEnabled) &&
        hl7MessageQuery.data?.totalCount
      ) {
        const hl7Message = hl7MessageQuery.data.data.find(
          (x) => x.outgoingObjectId === dataBlockId && x.outgoingFileId == null,
        );
        if (hl7Message) {
          transportInfo = {
            ...transportInfo,
            isHL7: true,
            hl7Status: getTransportStatus(hl7Message),
            hl7Date: hl7Message.dateTime,
          };
        }
      }
      if (
        claims.permissions.gdt &&
        gdtSettings?.enabled &&
        gdtMessageQuery.data?.totalCount
      ) {
        const gdtMessage = gdtMessageQuery.data.data.find(
          (x) => x.outgoingObjectId === dataBlockId && x.outgoingFileId == null,
        );
        if (gdtMessage) {
          transportInfo = {
            ...transportInfo,
            isGDT: true,
            gdtStatus: getTransportStatus(gdtMessage),
            gdtDate: gdtMessage.dateTime,
          };
        }
      }
      if (transportInfo.isGDT || transportInfo.isHL7) {
        return transportInfo;
      }
      return undefined;
    }

    function getIsNotDeletable(typeId: number) {
      const type = dataBlockTypes.find((x) => x.id == typeId);
      return type?.variant === DataBlockVariant.Case &&
        type?.value === caseInformationDataBlockTypeName
        ? true
        : false;
    }

    function getShowStatistic(typeId: number) {
      const type = dataBlockTypes.find((x) => x.id == typeId);
      return type?.showStatistic;
    }

    const caseItems = useMemo(() => {
      const caseResponse = caseQuery.data;
      if (!caseResponse) {
        return [];
      }
      const caseDataItem: CaseItem = {
        id: `case-data_${caseId}`,
        date: caseResponse.startDate,
        type: CaseItemType.caseData,
        item: caseResponse,
        subitems: (dataBlocksQuery.data?.data ?? [])
          .filter(
            (dataBlock) =>
              getVariant(dataBlock.typeId) === DataBlockVariant.Case,
          )
          // blocks should be ordered by adding time (the one added the first should always be the first in a list)
          .sort((x, y) => (x.id > y.id ? 1 : -1))
          .map((caseBlock) => {
            return {
              key: `c_${caseId}_b_${caseBlock.id}`,
              id: `case-data_${caseId}_block_${caseBlock.id}`,
              date: new Date(),
              type: SubCaseItemType.caseInfo,
              item: caseBlock,
              isNotDeletable: getIsNotDeletable(caseBlock.typeId),
              isStatisticCompleted: caseBlock.isStatisticCompleted,
              showStatistic: getShowStatistic(caseBlock.typeId),
              isGDTEnabled: getIsGdtEnabled(caseBlock.typeId),
              isHL7Enabled: getIsHL7Enabled(caseBlock.typeId),
              transportInfo: getTransportInfo(caseBlock.id),
            };
          }),
      };

      const measurementDataBlocks = (dataBlocksQuery.data?.data ?? []).filter(
        (dataBlock) =>
          getVariant(dataBlock.typeId) === DataBlockVariant.Measurement ||
          getVariant(dataBlock.typeId) === DataBlockVariant.DeviceMeasurement,
      );

      const measurementItems: CaseItem[] = measurementDataBlocks
        .filter((measurement) => !measurement.visitId)
        .map((measurement) => {
          return {
            id: `measurement_${measurement.id}`,
            date: measurement.begin!,
            type: CaseItemType.measurement,
            item: measurement,
            subitems: [],
            isGDTEnabled: getIsGdtEnabled(measurement.typeId),
            isHL7Enabled: getIsHL7Enabled(measurement.typeId),
            transportInfo: getTransportInfo(measurement.id),
            isStatisticCompleted: measurement.isStatisticCompleted,
            showStatistic: getShowStatistic(measurement.typeId),
          };
        });

      const visitItems: CaseItem[] = (visitsQuery.data ?? []).map(
        (visit): CaseItem => {
          const visitMeasurementItems = measurementDataBlocks
            .filter((measurement) => measurement.visitId === visit.id)
            .sort((a, b) => b.begin!.getTime() - a.begin!.getTime())
            .map((measurement) => {
              return {
                key: `v_${visit.id}_m_${measurement.id}`,
                id: `visit_${visit.id}_measurement_${measurement.id}`,
                date: measurement.begin!,
                type: SubCaseItemType.measurement,
                item: measurement,
                isGDTEnabled: getIsGdtEnabled(measurement.typeId),
                isHL7Enabled: getIsHL7Enabled(measurement.typeId),
                transportInfo: getTransportInfo(measurement.id),
                isStatisticCompleted: measurement.isStatisticCompleted,
                showStatistic: getShowStatistic(measurement.typeId),
              };
            });
          const visitDataItems = (dataBlocksQuery.data?.data ?? [])
            .filter(
              (dataBlock) =>
                getVariant(dataBlock.typeId) === DataBlockVariant.Visit &&
                dataBlock.visitId === visit.id,
            )
            // blocks should be ordered by adding time (the one added the first should always be the first in a list)
            .sort((x, y) => (x.id > y.id ? 1 : -1))
            .map((visitBlock) => {
              return {
                key: `v_${visitBlock.visitId}_b_${visitBlock.id}`,
                id: `visit_${visitBlock.visitId}_block_${visitBlock.id}`,
                date: new Date(),
                type:
                  visitInformationDataBlockType?.id === visitBlock.typeId
                    ? SubCaseItemType.visitPreview
                    : SubCaseItemType.visitInfo,
                item: visitBlock,
                isStatisticCompleted: visitBlock.isStatisticCompleted,
                showStatistic: getShowStatistic(visitBlock.typeId),
                isGDTEnabled: getIsGdtEnabled(visitBlock.typeId),
                isHL7Enabled: getIsHL7Enabled(visitBlock.typeId),
                transportInfo: getTransportInfo(visitBlock.id),
              };
            });
          (visit as any).customFields = {};
          (visit as any).resources = [];
          return {
            id: `visit_${visit.id}`,
            date: visit.start,
            type: CaseItemType.visit,
            item: visit as VisitListDto & {
              values: { [key: string]: any };
              resources: string[];
            },
            subitems: visitDataItems.concat(visitMeasurementItems),
          };
        },
      );

      const caseItems: CaseItem[] = visitItems
        .concat(measurementItems)
        .sort((a, b) => b.date.getTime() - a.date.getTime());
      caseItems.unshift(caseDataItem);
      return caseItems;
    }, [
      casesQuery.data,
      caseQuery.data,
      visitsQuery.data,
      dataBlocksQuery.data,
      hl7MessageQuery.data,
      gdtMessageQuery.data,
      gdtTemplateRules,
      hl7TemplateRules,
    ]);

    const isObserversActive = useRef(false);
    useEffect(
      function setupIntersectionObserverToHighlightActiveCaseItem() {
        const initialUrl = location.pathname;

        // stores information if the block is visible currently. Index is position of the block (from the top)
        const itemVisibilities: { id: string; visible: boolean }[] = [];
        // stores the position of a block. Key is block key, value is position of a block
        const itemIndexes: Record<string, number> = {};
        let index = 0;

        function addItemToIndexesAndVisibilities(id: string) {
          itemIndexes[id] = index;
          itemVisibilities[index] = { id: id, visible: false };
          index++;
        }

        caseItems.forEach((x) => {
          addItemToIndexesAndVisibilities(x.id);
          if (x.subitems) {
            x.subitems.forEach((subItem) =>
              addItemToIndexesAndVisibilities(subItem.id),
            );
          }
        });

        const observer = new IntersectionObserver((entries) => {
          if (getCurrentUrl() !== initialUrl) return;

          const currentlyOpenedTabId = getTabId(location);
          if (currentlyOpenedTabId !== tabs.tabId) {
            isObserversActive.current = false;
            observer.disconnect();
            return;
          }

          entries.forEach((entry) => {
            const id = entry.target.getAttribute('data-case-item-id');
            if (!id) {
              return;
            }
            const isVisible = entry.intersectionRatio > 0;

            const item = itemVisibilities[itemIndexes[id]];
            if (!item) {
              console.error(
                'item is undefined!',
                caseId,
                itemVisibilities,
                itemIndexes,
                id,
              );
              return;
            }
            item.visible = isVisible;

            let firstIndex = itemVisibilities.findIndex((x) => x.visible);
            if (firstIndex < 0) {
              // It fixes #23953: Fix scroll behavior.
              // When we scroll as down as possible, then switch to other Case, scroll down, and switch back.
              // Previously there were issues that nothing was selected in case tree.
              firstIndex = itemVisibilities.length - 1;
            }

            const firstVisibleElementId = itemVisibilities[firstIndex].id;
            const highlightedData = itemVisibilities
              .map((x) => x.id)
              .slice(0, firstIndex);

            const parentId = getItemPrefix(firstVisibleElementId);

            const highlightedHeaders = highlightedData
              .filter((x) => !x.startsWith(parentId))
              .map((x) => getItemPrefix(x) + '_header')
              .filter((v, i, a) => a.indexOf(v) === i);

            document!.querySelectorAll(`[data-case-item-link]`).forEach((x) => {
              x.removeAttribute('data-selected');
              x.removeAttribute('data-highlighted-inner-line');
              x.removeAttribute('data-highlighted-outside-line');
            });

            // add data-selected attribute to a first selected node
            const selectedContentsTreeItem = document!.querySelector(
              `[data-case-item-link="${firstVisibleElementId}"]`,
            );

            highlightedData.forEach((id) =>
              document!
                .querySelector(`[data-case-item-link="${id}"]`)
                ?.setAttribute(
                  id.startsWith(parentId)
                    ? 'data-highlighted-inner-line'
                    : 'data-highlighted-outside-line',
                  'true',
                ),
            );

            highlightedHeaders.forEach((id) =>
              document!
                .querySelector(`[data-case-item-link="${id}"]`)
                ?.setAttribute('data-highlighted-inner-line', 'true'),
            );
            document!
              .querySelector(`[data-case-item-link="${parentId}_header"]`)
              ?.setAttribute('data-selected', 'true');

            selectedContentsTreeItem?.setAttribute('data-selected', 'true');
            selectedContentsTreeItem?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            navigate(
              { hash: `caseItem${firstVisibleElementId}` },
              { replace: true },
            );
          });
        });

        // Track all sections that have an `id` applied
        document
          .querySelector(`[data-case-id='${caseId}']`)
          ?.querySelectorAll(`[data-case-item-id]`)
          ?.forEach((section) => {
            observer.observe(section);
          });
        isObserversActive.current = true;

        return () => {
          observer.disconnect();
        };
      },
      [caseItems],
    );

    const changeCurrentItem = useCallback(
      (itemId: string) => {
        const elementId = `caseItem${itemId}`;
        document.getElementById(elementId)?.scrollIntoView();
        navigate({ hash: elementId }, { replace: true });
      },
      [dispatch, history, navigate],
    );

    useEffect(
      function focusCaseItemBasedOnURL() {
        if (
          searchedItem &&
          caseItems.find(
            (item) =>
              item.id === searchedItem ||
              item.subitems?.map((s) => s.id).includes(searchedItem),
          )
        ) {
          if (tabId) {
            // clear tab.search so when we switch tabs this won't fire again
            dispatch(
              NavigationActions.queryStringChanged({ id: tabId, search: '' }),
            );
          }
          changeCurrentItem(searchedItem);
          setSearchedItem(null);
        }
      },
      [caseItems, searchedItem, tabId],
    );

    return (
      <>
        <Loading
          testId={'case-tab'}
          loading={
            casesQuery.isLoading ||
            visitsQuery.isLoading ||
            caseQuery.isLoading ||
            dataBlocksQuery.isLoading ||
            isCaseBeingDeleted ||
            isCaseBeingAdded ||
            false
          }
          title={i18next.t('data_loading')}
          flex={true}
        >
          {(!casesQuery.data || casesQuery.data.data.length > 0) && (
            <div className={styles.caseBar}>
              <CaseBar
                patientId={patientId}
                currentCaseId={props.caseId}
                addCase={addCase}
                openCaseTab={openCaseTabOrEmpty}
              />
            </div>
          )}
          {(casesQuery.data?.data ?? []).map((caseItem) => {
            return (
              <Freeze freeze={caseItem.id !== caseId} key={caseItem.id}>
                <QueryMetaProvider meta={{ caseId: caseItem.id }}>
                  <div
                    className={styles.caseData}
                    data-test-id={'case-container'}
                    data-case-id={caseId}
                  >
                    <CaseContents
                      patientId={patientId}
                      caseId={caseItem.id}
                      caseItems={caseItems}
                      changeCurrentItem={changeCurrentItem}
                      removeCase={removeCase}
                      reopenCase={reopenCase}
                      isReadOnly={caseQuery.data?.isClosed ?? false}
                    />
                    <CaseBlocks
                      patientId={props.patientId}
                      caseId={caseItem.id}
                      caseItems={caseItems}
                      changeCurrentItem={changeCurrentItem}
                      currentItemId={currentItemId}
                      isReadOnly={caseQuery.data?.isClosed ?? false}
                    />
                  </div>
                </QueryMetaProvider>
              </Freeze>
            );
          })}

          {casesQuery.data?.data.length === 0 && (
            <div className={tableStyles.emptyList}>
              <div className={tableStyles.emptyListText}>
                {i18next.t('empty')}
              </div>
              <Button
                iconComponent={<AddCaseIcon />}
                color={ButtonColor.Default}
                width={ButtonWidth.Content}
                title={i18next.t('add_button')}
                onClick={addCase}
                testId="add_first_case_button"
              />
            </div>
          )}
        </Loading>
      </>
    );
  },
);
